import { z } from 'zod';

// Mirror of ObservationDataSourceValues in the API.
export const MARKET_PRICE_DATA_SOURCES = [
  'morning_placeholder',
  'field',
  'scrape',
  'field_correction',
] as const;
export type MarketPriceDataSource = (typeof MARKET_PRICE_DATA_SOURCES)[number];

// ---------- Server response shapes ----------

export const marketPriceSchema = z.object({
  id: z.string(),
  priceSeriesId: z.string(),
  priceDate: z.string(), // YYYY-MM-DD
  cropId: z.string(),
  cropCode: z.string(),
  cropName: z.string(),
  varietyId: z.string(),
  varietyCode: z.string(),
  varietyName: z.string(),
  apmcId: z.string(),
  apmcCode: z.string(),
  apmcName: z.string(),
  minPrice: z.number().nullable(),
  modalPrice: z.number().nullable(),
  maxPrice: z.number().nullable(),
  commodityArrivals: z.number().nullable(),
  commodityTraded: z.number().nullable(),
  unit: z.string(),
  dataSource: z.enum(MARKET_PRICE_DATA_SOURCES),
  sourceCode: z.string().nullable(),
  sourceName: z.string().nullable(),
  isActive: z.boolean(),
  notes: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type MarketPrice = z.infer<typeof marketPriceSchema>;

// ---------- List query params ----------

// Mirror of MarketPriceSortKeyValues in the API.
export const MARKET_PRICE_SORT_KEYS = [
  'date',
  'min',
  'modal',
  'max',
  'arrivals',
] as const;
export type MarketPriceSortKey = (typeof MARKET_PRICE_SORT_KEYS)[number];

export type SortDirection = 'asc' | 'desc';

// ---------- Edit form (PATCH /market-prices/:id) ----------
//
// Inputs are HTML <input type="number">; react-hook-form gives us strings.
// The preprocess maps blank → undefined so zod raises "Required" instead of
// "Numbers only". All four fields are required, non-negative, ≤ 2 decimals
// (matching DB Decimal(10,2)). Cross-field min ≤ modal ≤ max in superRefine.

const decimalSchema = z.preprocess(
  (v) => {
    if (v === '' || v == null) return undefined; // → triggers "Required"
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isNaN(n) ? v : n;
  },
  z
    .number({
      required_error: 'Required',
      invalid_type_error: 'Numbers only',
    })
    .nonnegative('Must be 0 or greater')
    .refine(
      (n) => Math.abs(n * 100 - Math.round(n * 100)) < 1e-9,
      { message: 'At most 2 decimal places' },
    ),
);

export const marketPriceEditFormSchema = z
  .object({
    minPrice: decimalSchema,
    modalPrice: decimalSchema,
    maxPrice: decimalSchema,
    commodityArrivals: decimalSchema,
  })
  .superRefine((vals, ctx) => {
    const { minPrice, modalPrice, maxPrice } = vals;
    // Guards: per-field validation may have failed → values can be missing.
    // The cross-field rule only applies when all three numbers parsed.
    if (typeof minPrice !== 'number' || typeof modalPrice !== 'number' || typeof maxPrice !== 'number') {
      return;
    }
    if (minPrice > maxPrice) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Min cannot exceed Max',
        path: ['minPrice'],
      });
    }
    if (minPrice > modalPrice) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Min cannot exceed Modal',
        path: ['minPrice'],
      });
    }
    if (modalPrice > maxPrice) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Modal cannot exceed Max',
        path: ['modalPrice'],
      });
    }
  });
export type MarketPriceEditFormValues = z.infer<typeof marketPriceEditFormSchema>;

export interface ListMarketPricesParams {
  page: number;
  pageSize: number;
  cropId?: number;
  apmcId?: number;
  varietyId?: number;
  dateFrom?: string; // ISO date
  dateTo?: string; // ISO date
  sortBy?: MarketPriceSortKey;
  sortDir?: SortDirection;
  // 'true' = published rows; 'false' = pending placeholder rows. Server
  // accepts both string values; absence defaults to 'true' on the server side.
  isActive?: 'true' | 'false';
}
